import React, { useCallback, useMemo, useState } from 'react';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelPanel from '../../../components/voxel/Surfaces/VoxelPanel';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelAdvancedTextSearch from '../../../components/voxel/Search/VoxelAdvancedTextSearch';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import { CommandGq } from '../../../graphql/users/queries/useGetTeamConnectionsGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import NewConnectionDialog from './NewConnectionDialog';
import TeamConnectionsTable from './TeamConnectionsTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug } from '@fortawesome/pro-regular-svg-icons';

export interface TeamConnectionsPageProps {
  team: CurrentTeamGq;
  user: CurrentUserGq;
  availableUsers: PublicUserGq[];
  reload: () => Promise<unknown>;
}

export type ReloadFunction = () => Promise<unknown>;

export const TeamConnectionsPage: React.FC<TeamConnectionsPageProps> = ({
  team,
  user,
  reload,
  availableUsers,
}) => {
  // Get connected teams
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounceValue(searchTerm, 200);
  const [paging, setPaging] = useState<PagingInputGq>({ page: 0, amountPerPage: 10 });
  const command = useMemo(
    (): CommandGq => ({
      pagingInput: paging,
      searchTerm: debouncedSearchTerm,
    }),
    [debouncedSearchTerm, paging]
  );
  const { query, teamConnections, pagingResult } =
    usersApiService.queries.useGetTeamConnections(command);
  const combinedReload = useCallback(
    () => Promise.all([query.refetch(), reload()]),
    [query.refetch, reload]
  );

  const [newConnectionOpen, setNewConnectionOpen] = useState(false);

  if (team.voxelTeam) {
    return (
      <div>
        <VoxelText>Team connections is disabled for the support team</VoxelText>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-8 container">
      {newConnectionOpen && (
        <NewConnectionDialog
          close={() => setNewConnectionOpen(false)}
          connections={teamConnections}
          team={team}
          reload={combinedReload}
        />
      )}
      {!user.team.accessUserCanEditBasicSettings && (
        <VoxelAlert severity='error' size='sm' iconSize='lg'>You do not have permission to edit team connection settings</VoxelAlert>
      )}
      <div className="w-full flex flex-row gap-24 items-start justify-between">
        <div className="grow">
          <VoxelText variant="title2">Team connections</VoxelText>
          <VoxelText variant="bread-normal" paddingTop>
            Team connections facilitate collaboration. One team (e.g. client) shares all of its
            project data with another team (e.g. agency). In return the sharing team receives a free
            sponsored subscription plan.
          </VoxelText>
        </div>
        <div className="grow-0 flex flex-row items-center gap-3">
          <VoxelButton
            title="New connection"
            color="primary"
            onClick={() => setNewConnectionOpen(true)}
            endIcon={<FontAwesomeIcon icon={faPlug} />}
          />
        </div>
      </div>
      {/* Table section */}
      <div className="w-full flex flex-col gap-4">
        <VoxelPanel className="w-full">
          <VoxelAdvancedTextSearch
            size="sm"
            placeholder="Team"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            deleteValue={() => setSearchTerm('')}
            widthClass="w-52 ml-3 mt-3"
          />
          <TeamConnectionsTable
            team={team}
            connections={teamConnections ?? []}
            pagingInput={paging}
            pagingResult={pagingResult}
            setPaging={setPaging}
            reload={combinedReload}
            user={user}
          />
        </VoxelPanel>
      </div>
    </div>
  );
};

export default TeamConnectionsPage;
